import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router';

// Layouts
const AuthLayout = React.lazy(() => import('layouts/auth'));
const PrivateLayout = React.lazy(() => import('layouts/private'));
const SidebarLayout = React.lazy(() => import('layouts/private/sidebar'));
const SelfCheckoutLayout = React.lazy(() => import('pages/orders/self-checkout/layout'));
const GlobalSettingsLayout = React.lazy(() => import('pages/global-settings/layout'));

// Private Pages
const DashboardPage = React.lazy(() => import('pages/dashboard'));

const HelpPage = React.lazy(() => import('pages/help'));
const NotFoundPage = React.lazy(() => import('pages/errors/not-found'));
const ForbiddenPage = React.lazy(() => import('pages/errors/forbidden'));

// --------------------------------------

import { definitions as authenticationDefinitions } from 'routes/def-authentication';
import { definitions as globalSettingsDefinitions } from 'routes/def-global-settings';
import { definitions as trendsDefinitions } from 'routes/def-trends';
import { definitions as productsDefinitions } from 'routes/def-products';
import { definitions as syncManagerDefinitions } from 'routes/def-sync-manager';
import { definitions as partnersDefinitions } from 'routes/def-partners';
import { definitions as reportsDefinitions } from 'routes/def-reports';
import { definitions as wineSubmissionsDefinitions } from 'routes/def-wine-submissions';
import { definitions as suppliersDefinitions } from 'routes/def-suppliers';
import { definitions as wineriesDefinitions } from 'routes/def-wineries';
import { definitions as cellarxModelingDefinitions } from 'routes/def-cellarx-modeling';
import { definitions as analyticsDefinitions } from 'routes/def-analytics';
import { definitions as salesCommissionsDefinitions } from 'routes/def-sales-commissions';
import { definitions as salesStaffDefinitions } from 'routes/def-sales-staff';
import { definitions as usersDefinitions, miscDefinitions as usersMiscDefinitions } from 'routes/def-users';
import { definitions as scorecardDefinitions } from 'routes/def-scorecard';
import { definitions as posDefinitions } from 'routes/def-pos';
import {
  privateDefinitions as wineClubCampaignPrivateDefinitions,
  publicDefinitions as wineClubCampaignPublicDefinitions
} from 'routes/def-wine-club-campaigns';

import {
  privateDefinitions as tastingRoomPrivateDefinitions,
  publicDefinitions as tastingRoomPublicDefinitions
} from 'routes/def-tasting-room';

import {
  privateDefinitions as ordersPrivateDefinitions,
  publicDefinitions as ordersPublicDefinitions
} from 'routes/def-orders';

import { definitions as selfCheckoutDefinitions } from 'routes/def-selfcheckout';

import { useRoutes } from 'routes/use-routes';

export default function AppRouter() {
  const authenticationRoutes = useRoutes('/', authenticationDefinitions);
  const globalSettingsRoutes = useRoutes('/global-settings', globalSettingsDefinitions);
  const trendsRoutes = useRoutes('/trends', trendsDefinitions);
  const productsRoutes = useRoutes('/products', productsDefinitions);
  const syncManagerRoutes = useRoutes('/sync-manager', syncManagerDefinitions);
  const wineSubmissionsRoutes = useRoutes('/wine-submissions', wineSubmissionsDefinitions);
  const ordersPrivateRoutes = useRoutes('/orders', ordersPrivateDefinitions);
  const ordersPublicRoutes = useRoutes('/orders', ordersPublicDefinitions);
  const reportsRoutes = useRoutes('/reports', reportsDefinitions);
  const partnersRoutes = useRoutes('/partners', partnersDefinitions);
  const suppliersRoutes = useRoutes('/suppliers', suppliersDefinitions);
  const wineriesRoutes = useRoutes('/wineries', wineriesDefinitions);
  const cellarxModelingRoutes = useRoutes('/cellarx-modeling', cellarxModelingDefinitions);
  const analyticsRoutes = useRoutes('/analytics', analyticsDefinitions);
  const tastingRoomPrivateRoutes = useRoutes('/tasting-room', tastingRoomPrivateDefinitions);
  const tastingRoomPublicRoutes = useRoutes('/tasting-room', tastingRoomPublicDefinitions);
  const salesCommissionsRoutes = useRoutes('/sales-commissions', salesCommissionsDefinitions);
  const salesStaffRoutes = useRoutes('/sales-staff', salesStaffDefinitions);
  const scorecardRoutes = useRoutes('/scorecard', scorecardDefinitions);
  const posRoutes = useRoutes('/pos', posDefinitions);
  const wineClubCampaignPrivateRoutes = useRoutes('/wine-clubs', wineClubCampaignPrivateDefinitions);
  const wineClubCampaignPublicRoutes = useRoutes('/wine-clubs', wineClubCampaignPublicDefinitions);
  const selfCheckoutRoutes = useRoutes('/selfcheckout/:partnerAccessToken?', selfCheckoutDefinitions);

  const usersRoutes = useRoutes('/users', usersDefinitions);
  const usersMiscRoutes = useRoutes('/', usersMiscDefinitions);

  return (
    <BrowserRouter>
      <Suspense>
        <Routes>
          {tastingRoomPublicRoutes}
          {ordersPublicRoutes}
          {wineClubCampaignPublicRoutes}

          <Route element={<AuthLayout />}>{authenticationRoutes}</Route>
          <Route element={<SelfCheckoutLayout />}>{selfCheckoutRoutes}</Route>

          <Route element={<PrivateLayout />}>
            {tastingRoomPrivateRoutes}

            <Route element={<SidebarLayout />}>
              <Route
                index
                path='/'
                element={<DashboardPage />}
              />

              <Route element={<GlobalSettingsLayout />}>{globalSettingsRoutes}</Route>

              {trendsRoutes}
              {productsRoutes}
              {syncManagerRoutes}
              {ordersPrivateRoutes}
              {partnersRoutes}
              {reportsRoutes}
              {wineSubmissionsRoutes}
              {suppliersRoutes}
              {wineriesRoutes}
              {cellarxModelingRoutes}
              {analyticsRoutes}

              {salesCommissionsRoutes}
              {salesStaffRoutes}
              {scorecardRoutes}
              {posRoutes}
              {wineClubCampaignPrivateRoutes}

              {usersRoutes}
              {usersMiscRoutes}

              {miscRoutes()}
            </Route>
          </Route>

          <Route
            path='/404'
            element={<NotFoundPage />}
          />
          <Route
            path='/forbidden'
            element={<ForbiddenPage />}
          />

          <Route
            path='*'
            element={<NotFoundPage />}
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

const miscRoutes = () => {
  return (
    <>
      <Route
        path='/help'
        element={<HelpPage />}
      />
    </>
  );
};
